import { EssenceVessel, Loadout, useItemTransferContext } from '@sorrem-client/ui/components';
import { useAbilityPickerMenu } from '@sorrem-client/ui/features/AbilityPicker';
import { useIsRunHudTourPending } from '@sorrem-client/ui/features/GuidedTour';
import { listCarriedSlotHotkeyIndexes, withCarriedSlotHotkeyToggled } from '@sorrem-client/ui/shared/action-bar';
import { useLoadoutActions } from '@sorrem-client/ui/features/loadout-actions';
import { useLoadoutEquipment } from '@sorrem-client/ui/features/loadout-equipment';
import { useActionBarTargets, useDrawActionCallout } from '@sorrem-client/ui/hooks';
import { useCharacterHomeCharacter } from '../CharacterHomeItemTransfer';
import { getWieldedLoadout } from '@sorrem/shared';
import { useCallback, useMemo, useState } from 'react';

interface CharacterHomeLoadoutProps {
    className?: string;
    /** A skill waiting for its action slot (see `useSkillHotkeyAssignment`); action frames then pick its slot. */
    assigningSkillId?: string | null;
    /** Called with the action index picked for `assigningSkillId`. */
    onAssignSkill?: (index: number) => void;
    /** An action frame to outline, e.g. the one a skill was just assigned to. */
    confirmedActionIndex?: number | null;
    /** A carried slot waiting for its action slot (see `useCarriedSlotHotkeyAssignment`); action frames then pick its slot. */
    linkingSlotIndex?: number | null;
    /** Called with the action index picked for `linkingSlotIndex`. */
    onLinkCarriedSlot?: (index: number) => void;
}

/**
 * The selected character's essence and equipment, with their banked essence
 * in the vessel the right panel slides out. Frames accept the item
 * selected in the inventory grids; clicking an action frame while a carried
 * item is selected links that hotkey to the item's carried slot (clicking
 * again unlinks it). While a skill waits for an action slot, clicking an
 * action frame (or pressing its key) assigns it there instead, and the
 * equipment frames ignore clicks. While a carried slot waits for an action
 * slot, clicking an action frame (or pressing its key) links it there.
 * Right-clicking an action frame opens the ability picker on it.
 */
export function CharacterHomeLoadout({
    className,
    assigningSkillId = null,
    onAssignSkill,
    confirmedActionIndex = null,
    linkingSlotIndex = null,
    onLinkCarriedSlot,
}: CharacterHomeLoadoutProps): JSX.Element {
    const character = useCharacterHomeCharacter();
    const { selectedItemId, clearSelectedItem } = useItemTransferContext();
    const { targets, setTargets } = useActionBarTargets();
    const [isVesselOpen, setIsVesselOpen] = useState(false);

    const carriedItems = useMemo(() => character?.carriedItems ?? [], [character]);
    // Home has storage too: a stored item can be selected and put on.
    const selectableItems = useMemo(() => [...carriedItems, ...(character?.storedItems ?? [])], [carriedItems, character]);
    // Frames and details show the gear as wielded: an item the character falls short of comes back weakened.
    const wieldedLoadout = useMemo(() => (character ? getWieldedLoadout(character) : undefined), [character]);
    const abilityPicker = useAbilityPickerMenu(character?.skills, wieldedLoadout);
    const equipment = useLoadoutEquipment(wieldedLoadout, selectableItems);
    const caster = useMemo(
        () => (character ? { essence: character.essence, skills: character.skills, wieldedLoadout } : undefined),
        [character, wieldedLoadout],
    );
    const { actions, actionDetails } = useLoadoutActions(targets, carriedItems, caster);

    const isAssigningSkill = assigningSkillId !== null;
    const isLinkingCarriedSlot = linkingSlotIndex !== null;
    // Outlines the slot a skill just went to, or the one a waiting carried slot is linked to now.
    const selectedActions = useMemo(() => {
        if (confirmedActionIndex !== null) {
            return [confirmedActionIndex];
        }
        return linkingSlotIndex === null
            ? []
            : listCarriedSlotHotkeyIndexes(targets, linkingSlotIndex);
    }, [confirmedActionIndex, linkingSlotIndex, targets]);

    const handleActionActivate = useCallback((index: number): void => {
        if (isAssigningSkill) {
            onAssignSkill?.(index);
            return;
        }
        if (isLinkingCarriedSlot) {
            onLinkCarriedSlot?.(index);
            return;
        }
        const carriedSlotIndex = selectedItemId === null ? -1 : carriedItems.findIndex((entry) => entry?.id === selectedItemId);
        if (carriedSlotIndex < 0) {
            return;
        }
        setTargets(withCarriedSlotHotkeyToggled(targets, index, carriedSlotIndex));
        clearSelectedItem();
    }, [carriedItems, clearSelectedItem, isAssigningSkill, isLinkingCarriedSlot, onAssignSkill, onLinkCarriedSlot, selectedItemId, setTargets, targets]);

    const drawCallout = useDrawActionCallout();
    // A new player first hears of Draw after the intro run's HUD tour, not before it.
    const isRunHudTourPending = useIsRunHudTourPending();

    return (
        <Loadout
            className={className}
            canRebindKeys
            actionCallout={isRunHudTourPending ? null : drawCallout}
            essence={character?.essence ?? 0}
            maxEssence={character?.maxEssence ?? 0}
            onActionActivate={handleActionActivate}
            actions={actions}
            actionDetails={actionDetails}
            {...abilityPicker.loadoutProps}
            hasHighlightedActions={isAssigningSkill || isLinkingCarriedSlot}
            selectedActions={selectedActions}
            {...equipment}
            // While a skill is being put on an action slot, the equipment frames sit still.
            onSlotDrop={isAssigningSkill ? undefined : equipment.onSlotDrop}
            onSlotSelect={isAssigningSkill ? undefined : equipment.onSlotSelect}
            onSlotMenu={isAssigningSkill ? undefined : equipment.onSlotMenu}
            highlightedSlots={isAssigningSkill ? undefined : equipment.highlightedSlots}
            selectedSlots={isAssigningSkill ? undefined : equipment.selectedSlots}
            vessel={character === null ? undefined : <EssenceVessel essence={character.permanentEssence} />}
            isVesselOpen={isVesselOpen}
            onVesselOpenChange={setIsVesselOpen}
        />
    );
}
